import classnames from 'classnames';
import PropTypes from 'prop-types';

import ArrowButton from 'shared/components/button/ArrowButton';
import Button from 'shared/components/button/Button';
import CloseButton from 'shared/components/button/CloseButton';
import Icon from 'shared/components/icon/JokerWiz';
import SelectInput from 'shared/components/input/SelectInput';
import Loader from 'shared/components/loader/Loader';
import {
  Layouts,
  MaxPrices,
  MaxPriceTypes,
  RiskLevels,
  WIZARD_STEP_CREATE_SYSTEM,
  WIZARD_STEP_HORSES,
  WIZARD_STEP_SYSTEM_TYPE,
  WizardSteps,
} from 'shared/constants/JokerWizConstants';
import { formatCalendar } from 'shared/utils/dateUtils';
import { getText } from 'shared/utils/Text';

/* eslint-disable-next-line css-modules/no-unused-class */
import styles from './index.module.scss';

const JokerWizDialog = ({
  customSpikes,
  defaultSpikes,
  disableSubmit,
  isLoading,
  maxPrice,
  maxPriceType,
  races,
  riskLevel,
  maxSpikes,
  minSpikes,
  stepIndex,
  system,
  layout,

  onAddCustomSpike,
  onChangeCustomSpike,
  onChangeLayout,
  onChangeMaxPrice,
  onChangeMaxPriceField,
  onChangeRiskLevel,
  onChangeSpikesNr,
  onChangeView,
  onClose,
  onDeleteCustomSpike,
  onResetCustomSpike,
  onSubmit,
}) => {
  const isLastStep = stepIndex === WizardSteps.length - 1;
  const currentStep = WizardSteps[stepIndex];

  const onSubmitClick = () =>
    isLastStep ? onSubmit() : onChangeView(stepIndex + 1);

  const onClickBack = () => onChangeView(stepIndex - 1);

  return (
    <div className={styles.wrapper}>
      <Loader backgroundAlpha="light" cover visible={isLoading} />
      <CloseButton
        border={false}
        className={styles.btnTop}
        invert
        onClick={onClose}
      />

      {stepIndex > 0 && (
        <ArrowButton
          backArrow
          border={false}
          className={styles.btnTop}
          invert
          onClick={onClickBack}
        />
      )}

      <div className={styles.container}>
        <Icon className={styles.icon} />

        <div className={styles.header}>
          {[
            system.betType,
            formatCalendar(system.firstPostTimePerBetType, {
              showTimeForToday: true,
              showDayOfWeek: false,
            }),
          ].join(', ')}
        </div>

        {currentStep === WIZARD_STEP_HORSES && (
          <>
            <div className={styles.subHeader}>
              {getText('jokerwiz-dialog-risklevel')}
            </div>
            <ul
              className={classnames(
                'fgrid',
                'fgrid--full',
                styles.nowrap,
                'fgrid--justifyCenter',
                'small-fgrid--1of3',
                'med-fgrid--1of3',
                'large-fgrid--1of3',
              )}
            >
              {RiskLevels.map(level => (
                <li
                  key={level}
                  className={classnames(
                    styles.btnContainer,
                    styles.riskLevel,
                    'fgrid-cell',
                    {
                      [styles.active]: level === riskLevel,
                    },
                  )}
                >
                  <Button
                    className={classnames(styles.btn)}
                    disabled={isLoading}
                    onClick={() => onChangeRiskLevel(level)}
                  >
                    {getText(
                      `jokerwiz-dialog-risklevel_${level.toLowerCase()}`,
                    )}
                  </Button>
                </li>
              ))}
            </ul>
            <div className={styles.subHeader}>
              {getText('jokerwiz-dialog-maxprice')}
            </div>
            <ul
              className={classnames(
                styles.prices,
                'fgrid',
                'fgrid--justifyCenter',
              )}
            >
              {MaxPrices.map(item => (
                <li
                  key={item}
                  className={classnames(
                    styles.btnContainer,
                    styles.maxPrice,
                    'fgrid-cell',
                    {
                      [styles.active]:
                        maxPrice === item &&
                        maxPriceType === MaxPriceTypes.button,
                    },
                  )}
                >
                  <Button
                    className={styles.btn}
                    disabled={isLoading}
                    onClick={() => onChangeMaxPrice(item, 'button')}
                  >
                    {item} {getText('currency-short')}
                  </Button>
                </li>
              ))}
            </ul>

            <label>
              <div className={styles.subHeader}>
                {getText('jokerwiz-dialog-usermaxprice')}
              </div>

              <div className={styles.inputGroup}>
                <input
                  className={classnames(styles.userInput, styles.shadowTop)}
                  disabled={isLoading}
                  maxLength={6}
                  onChange={onChangeMaxPriceField}
                  type="text"
                  value={maxPriceType === MaxPriceTypes.field ? maxPrice : ''}
                />
                <div className={styles.inputGroupAppend}>
                  <span
                    className={classnames(
                      styles.shadowTop,
                      styles.inputGroupText,
                    )}
                  >
                    {getText('currency-short')}
                  </span>
                </div>
              </div>
            </label>
          </>
        )}

        {currentStep === WIZARD_STEP_SYSTEM_TYPE && (
          <>
            <div className={styles.subHeader}>
              {getText('jokerwiz-dialog-single-horses')}
            </div>
            <div className={styles.subHeaderExplain}>
              {getText('jokerwiz-dialog-single-horses-explain')}
            </div>
            <ul
              className={classnames('fgrid', 'fgrid--justifyCenter', 'prices')}
            >
              {maxSpikes &&
                Array.from({ length: maxSpikes - minSpikes + 1 }).map(
                  (_, item) => (
                    <li
                      key={item}
                      className={classnames(
                        styles.btnContainer,
                        styles.maxPrice,
                        'fgrid-cell',
                        {
                          [styles.active]: defaultSpikes === item + minSpikes,
                        },
                      )}
                    >
                      <Button
                        className={styles.btn}
                        disabled={isLoading}
                        onClick={() => onChangeSpikesNr(item + minSpikes)}
                      >
                        {item + minSpikes}
                      </Button>
                    </li>
                  ),
                )}
            </ul>

            {defaultSpikes !== 0 && (
              <>
                <div className={styles.subHeader}>
                  {getText('jokerwiz-dialog-choose-single-horse')}
                </div>
                <div className={styles.subHeaderExplain}>
                  {getText('jokerwiz-dialog-choose-single-horse-explain')}
                </div>
              </>
            )}

            {races != null &&
              customSpikes.map((customSpike, spikeIdx) => (
                <div className={styles.selectContainer} key={spikeIdx}>
                  <SelectInput
                    className={styles.select}
                    disabled={isLoading}
                    onChange={e => {
                      const { value } = e.target;

                      if (value === '0') {
                        onResetCustomSpike(spikeIdx);
                      } else {
                        const [startNr, legNr] = value.split('_');
                        const newCustomSpike = {
                          legNr: +legNr,
                          startNr: +startNr,
                        };

                        onChangeCustomSpike(spikeIdx, newCustomSpike);
                      }
                    }}
                    theme="transparent"
                    value={
                      customSpike &&
                      `${customSpike.startNr}_${customSpike.legNr}`
                    }
                    width="auto"
                  >
                    <option value={0}>
                      {getText('jokerwiz-dialog-choose-single-horse-choose')}
                    </option>

                    {races.map((race, idx) => (
                      <optgroup
                        key={idx}
                        label={getText('jokerwiz-race-number', race.legNr)}
                      >
                        {race.starts
                          .sort((a, b) => b.distribution - a.distribution)
                          .map((start, idx) => {
                            const {
                              driver,
                              horse,
                              scratched = false,
                              startNo,
                            } = start;
                            const distribution =
                              start.distribution < 1
                                ? Math.round(start.distribution * 100) / 100
                                : Math.round(start.distribution);

                            const isDisabled =
                              scratched ||
                              (customSpikes.some(
                                s => s != null && s.legNr === race.legNr,
                              ) &&
                                customSpike?.legNr !== race.legNr);

                            const startNum = getText(
                              'jokerwiz-race-number',
                              race.legNr,
                            );

                            const text = `${startNum}: ${startNo} ${horse.name}, ${driver.name} ${driver.surname} (${distribution}%)`;

                            return (
                              <option
                                disabled={isDisabled}
                                key={idx}
                                value={`${startNo}_${race.legNr}`}
                              >
                                {text}
                              </option>
                            );
                          })}
                      </optgroup>
                    ))}
                  </SelectInput>
                  <CloseButton
                    border={false}
                    className={styles.сlose}
                    disabled={
                      customSpikes.length === 1 && customSpike === undefined
                    }
                    invert
                    onClick={() =>
                      customSpikes.length === 1
                        ? onResetCustomSpike(spikeIdx)
                        : onDeleteCustomSpike(spikeIdx)
                    }
                  />
                </div>
              ))}
            {races != null && customSpikes.length < defaultSpikes && (
              <Button
                className={styles.btnTransparent}
                disabled={isLoading}
                onClick={onAddCustomSpike}
              >
                {getText(
                  customSpikes.length === 0
                    ? 'jokerwiz-dialog-choose-single-horse-add'
                    : 'jokerwiz-dialog-choose-single-horse-add-more',
                )}
              </Button>
            )}
          </>
        )}

        {currentStep === WIZARD_STEP_CREATE_SYSTEM && (
          <>
            <div className={styles.systemTypeDetails}>
              {getText('jokerwiz-dialog-system-header')}
            </div>

            <div className={styles.subHeaderSystemType}>
              {getText('jokerwiz-dialog-system-explain')}
            </div>

            <ul
              className={classnames(
                'fgrid',
                'fgrid--full',
                'fgrid--justifyCenter',
              )}
            >
              {Layouts.map((layoutItem, i) => (
                <li
                  key={i}
                  className={classnames(
                    styles.btnContainer,
                    styles.fullWidth,
                    'fgrid-cell',
                    {
                      [styles.active]: layoutItem === layout,
                    },
                  )}
                >
                  <Button
                    className={classnames(styles.btn, styles.fullWidth)}
                    onClick={() => onChangeLayout(layoutItem)}
                  >
                    {getText(
                      `jokerwiz-dialog-system_${layoutItem.toLowerCase()}`,
                    )}
                  </Button>
                </li>
              ))}
            </ul>
          </>
        )}

        <Button
          className={classnames(styles.btn, styles.btnSubmit)}
          disabled={isLoading || disableSubmit}
          onClick={onSubmitClick}
        >
          {getText(
            isLastStep ? 'jokerwiz-dialog-createbet' : 'jokerwiz-dialog-next',
          )}
        </Button>
      </div>
    </div>
  );
};

JokerWizDialog.propTypes = {
  customSpikes: PropTypes.arrayOf(PropTypes.object).isRequired,
  defaultSpikes: PropTypes.number,
  disableSubmit: PropTypes.bool.isRequired,
  isLoading: PropTypes.bool.isRequired,
  layout: PropTypes.string.isRequired,
  maxPrice: PropTypes.oneOfType([PropTypes.number, PropTypes.string])
    .isRequired,
  maxPriceType: PropTypes.oneOf([MaxPriceTypes.button, MaxPriceTypes.field])
    .isRequired,
  maxSpikes: PropTypes.number,
  minSpikes: PropTypes.number,
  races: PropTypes.array,
  riskLevel: PropTypes.string.isRequired,
  stepIndex: PropTypes.number.isRequired,
  system: PropTypes.object.isRequired,

  onAddCustomSpike: PropTypes.func.isRequired,
  onChangeCustomSpike: PropTypes.func.isRequired,
  onChangeLayout: PropTypes.func.isRequired,
  onChangeMaxPrice: PropTypes.func.isRequired,
  onChangeMaxPriceField: PropTypes.func.isRequired,
  onChangeRiskLevel: PropTypes.func.isRequired,
  onChangeSpikesNr: PropTypes.func.isRequired,
  onChangeView: PropTypes.func.isRequired,
  onClose: PropTypes.func.isRequired,
  onDeleteCustomSpike: PropTypes.func.isRequired,
  onResetCustomSpike: PropTypes.func.isRequired,
  onSubmit: PropTypes.func.isRequired,
};

export default JokerWizDialog;
